/**
 * Envio do novo termo a UM paciente, pela tela do atendimento — HNS-1744.
 *
 * Quando a versão do termo do programa sobe, a operação reenvia o TCLE pela tela do
 * programa (AtualizacaoTermosPrograma), em lote. Este modal faz o mesmo envio para o
 * paciente aberto — é o equivalente a digitar só o id dele em "Restringir a
 * atendimentos" — sem sair do atendimento.
 *
 * O e-mail e o template de WhatsApp vêm preenchidos da configuração do programa:
 *
 *   "termos": {
 *     "atualizacao_paciente": {
 *       "email_assunto": "Atualização do Termo de Consentimento",
 *       "email_mensagem": "<p>Olá [nome_paciente], ...</p>",
 *       "template_whatsapp": "mensagem_novo_tcle_pacientes"
 *     }
 *   }
 *
 * Sem a configuração valem os mesmos textos padrão da tela do programa. O conteúdo
 * abre travado e só é alterado depois de clicar em "Editar", para o envio do dia a dia
 * não sair com um texto mexido sem querer.
 */
import React, { Component } from "react";
import { Modal, Form, Button, Alert, Spinner, Badge } from "react-bootstrap";
import { FaPaperPlane, FaPencilAlt, FaLock } from "react-icons/fa";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from "../../services/api";
import { getConfig } from "../../common/config";

const MySwal = withReactContent(Swal);

export const ASSUNTO_PADRAO = 'Atualização do Termo de Consentimento';
export const MENSAGEM_PADRAO =
    '<p>Olá,</p>'
    + '<p>O Termo de Consentimento do programa foi atualizado. '
    + 'Para continuar participando, pedimos que leia e aceite a nova versão.</p>';

class ModalEnviarNovoTermo extends Component {
    constructor(props) {
        super(props);
        this.state = this.estadoInicial();
    }

    componentDidUpdate(prevProps) {
        //cada abertura recomeça do texto configurado, descartando edições da vez anterior
        if (this.props.show && !prevProps.show) {
            this.setState(this.estadoInicial());
        }
    }

    estadoInicial = () => {
        const { configuracoes } = this.props;

        return {
            email_assunto: getConfig(configuracoes, 'termos.atualizacao_paciente.email_assunto', '') || ASSUNTO_PADRAO,
            email_mensagem: getConfig(configuracoes, 'termos.atualizacao_paciente.email_mensagem', '') || MENSAGEM_PADRAO,
            template_whatsapp: getConfig(configuracoes, 'termos.atualizacao_paciente.template_whatsapp', '') || '',
            editandoEmail: false,
            enviando: false,
            erro: '',
        };
    };

    montarPayload = () => {
        const { atendimentoId, programaId, programaVersao } = this.props;
        const { email_assunto, email_mensagem, template_whatsapp } = this.state;

        //mesmo payload da tela do programa restrito a um id: quem aceitou uma versão
        //anterior recebe o termo da versão atual e o aceite antigo vai para o histórico
        return {
            programa_id: programaId,
            termo_versao: programaVersao || '',
            atualizar_aceitos: true,
            atendimentos_ids: String(atendimentoId),
            email_assunto,
            email_mensagem,
            template_whatsapp: (template_whatsapp || '').trim() || undefined,
            preview: false,
        };
    };

    confirmarEnvio = async () => {
        const { programaVersao, pacienteNome, onEnviado, onClose } = this.props;
        const { email_assunto, email_mensagem, template_whatsapp } = this.state;

        if (!email_assunto.trim() || !email_mensagem.trim()) {
            this.setState({ erro: 'Informe o assunto e a mensagem do e-mail.' });
            return;
        }

        const confirma = await MySwal.fire({
            title: 'Enviar o novo termo?',
            html: `<p>Tem certeza que deseja enviar o termo da versão <strong>${programaVersao || '-'}</strong> `
                + `para <strong>${pacienteNome || 'este paciente'}</strong>?</p>`
                + `<p class="text-muted" style="font-size:.9em">O aceite anterior vai para o histórico e o paciente recebe `
                + `e-mail${(template_whatsapp || '').trim() ? ' e WhatsApp' : ''} com o novo termo.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, enviar',
            cancelButtonText: 'Cancelar',
        });

        if (!confirma.isConfirmed) return;

        this.setState({ enviando: true, erro: '' });

        try {
            const { data } = await api.post('/atendimentos/atualizacao_termos/', this.montarPayload());

            this.setState({ enviando: false });

            //o backend só notifica atendimento ativo do programa que aceitou outra versão -
            //se a lista voltou vazia, nada saiu e a tela precisa dizer isso
            if (!data || !data.total_atendimentos) {
                MySwal.fire({
                    title: 'Nenhum envio feito',
                    text: 'O paciente não se enquadra no reenvio (atendimento finalizado/cancelado ou já na versão atual). Atualize a tela e confira.',
                    icon: 'info',
                });
                return;
            }

            const enviado = (data.enviados || []).find(e => String(e.id) === String(this.props.atendimentoId));

            onClose();
            if (onEnviado) onEnviado(data);

            MySwal.fire({
                title: 'Novo termo enviado',
                html: enviado?.email
                    ? `<p>E-mail enviado para <strong>${enviado.email}</strong>${(template_whatsapp || '').trim() ? ' e WhatsApp disparado' : ''}.</p>`
                    : '<p>O termo foi gerado, mas o paciente não tem e-mail no cadastro — confira o contato.</p>',
                icon: enviado?.email ? 'success' : 'warning',
            });
        } catch (e) {
            console.error('Erro ao enviar o novo termo ao paciente:', e);
            this.setState({
                enviando: false,
                erro: e?.response?.data?.detail || 'Não foi possível enviar o novo termo.',
            });
        }
    };

    render() {
        const { show, onClose, programaVersao, versaoAtual } = this.props;
        const { email_assunto, email_mensagem, template_whatsapp, editandoEmail, enviando, erro } = this.state;

        return (
            <Modal show={show} onHide={enviando ? undefined : onClose} size="lg" centered>
                <Modal.Header closeButton={!enviando}>
                    <Modal.Title>Enviar novo termo ao paciente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="py-2" style={{ fontSize: '13px' }}>
                        O paciente aceitou a versão <Badge bg="secondary" className="fw-normal">{versaoAtual || 'sem versão'}</Badge>{' '}
                        e o programa está na versão <Badge bg="primary" className="fw-normal">{programaVersao || '-'}</Badge>.
                        O aceite anterior fica no histórico e o paciente recebe o link para aceitar o termo atual.
                    </Alert>

                    {erro && <Alert variant="danger" className="py-2">{erro}</Alert>}

                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <Form.Label className="mb-0">E-mail</Form.Label>
                            {!editandoEmail ? (
                                <Button variant="outline-secondary" size="sm" onClick={() => this.setState({ editandoEmail: true })} disabled={enviando}>
                                    <FaPencilAlt className="me-1" /> Editar
                                </Button>
                            ) : (
                                <small className="text-muted"><FaPencilAlt className="me-1" /> Editando o texto deste envio</small>
                            )}
                        </div>
                        <Form.Control
                            type="text"
                            className="mb-2"
                            placeholder="Assunto"
                            value={email_assunto}
                            disabled={!editandoEmail || enviando}
                            onChange={(e) => this.setState({ email_assunto: e.target.value })}
                        />
                        <Form.Control
                            as="textarea"
                            rows={6}
                            value={email_mensagem}
                            disabled={!editandoEmail || enviando}
                            onChange={(e) => this.setState({ email_mensagem: e.target.value })}
                        />
                        <Form.Text className="text-muted">
                            {!editandoEmail && <><FaLock className="me-1" />Texto padrão configurado no programa. </>}
                            Aceita HTML simples; <code>[nome_paciente]</code> e <code>[link_termo]</code> são substituídos no envio.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Template de WhatsApp (opcional)</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Deixe vazio para enviar só o e-mail"
                            value={template_whatsapp}
                            disabled={enviando}
                            onChange={(e) => this.setState({ template_whatsapp: e.target.value })}
                        />
                        <Form.Text className="text-muted">
                            Precisa ser um template aprovado e cadastrado na integração do programa. Vai só se o paciente tiver celular.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={enviando}>Cancelar</Button>
                    <Button variant="primary" onClick={this.confirmarEnvio} disabled={enviando}>
                        {enviando ? <Spinner animation="border" size="sm" className="me-1" /> : <FaPaperPlane className="me-1" />}
                        Enviar novo termo
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default ModalEnviarNovoTermo;
